import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Users,
  Vote,
  FileSpreadsheet,
  ShieldCheck,
  Map,
  Building,
  TrendingUp,
  Layers,
  ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_area_acres: 0,
    total_users: 0,
    total_consortiums: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    };
    fetchStats();
  }, []);

  const features = [
    {
      icon: Map,
      title: "Land Pooling",
      description:
        "Smart eligibility checks and automated document verification technology.",
      color: "bg-[#4E80FF]",
    },
    {
      icon: Users,
      title: "Consortium Management",
      description:
        "Complete digital administration of landowner consortiums with member coordination.",
      color: "bg-[#2952CC]",
    },
    {
      icon: FileText,
      title: "Agreement Formats",
      description:
        "Library of legal agreement templates with lawyer booking options.",
      color: "bg-[#4E80FF]",
    },
    {
      icon: Vote,
      title: "Voting Management",
      description:
        "Transparent weighted voting system for consortium decisions.",
      color: "bg-[#2952CC]",
    },
    {
      icon: FileSpreadsheet,
      title: "RFP & Bid Management",
      description:
        "Professional developer engagement through RFPs and bid comparison.",
      color: "bg-[#4E80FF]",
    },
    {
      icon: ShieldCheck,
      title: "Consent Management",
      description:
        "Secure OTP-verified consent system for legal compliance.",
      color: "bg-[#2952CC]",
    },
  ];

  const processSteps = [
    {
      step: 1,
      title: "Register & Verify",
      description: "Create account and submit your land documents for AI verification.",
    },
    {
      step: 2,
      title: "Check Eligibility",
      description: "Our AI system verifies documents and confirms eligibility for pooling.",
    },
    {
      step: 3,
      title: "Join Consortium",
      description: "Connect with other landowners and form or join a consortium.",
    },
    {
      step: 4,
      title: "Manage & Develop",
      description: "Use our tools for agreements, voting, RFPs, and project management.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/27062930/pexels-photo-27062930.jpeg"
            alt="Agricultural land aerial view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40" />
        </div>

        <div className="relative z-10 container-main py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
                <span className="w-2 h-2 bg-[#6B94FF] rounded-full animate-pulse" />
                Delhi &middot; DDA Land Pooling Policy 2018
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Transform Your Delhi Land Into
                <span className="text-[#6B94FF]"> Opportunity</span>
              </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-xl">
                Delhi&apos;s dedicated Land Pooling platform, built around
                DDA&apos;s notified policy. Form consortiums, run transparent
                voting, and manage the full 7-stage development journey in one
                place.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="btn-primary text-lg px-8"
                  onClick={() => navigate("/register")}
                  data-testid="hero-get-started-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 border-white text-white hover:bg-white hover:text-slate-900"
                  onClick={() => navigate("/about")}
                  data-testid="hero-learn-more-btn"
                >
                  Learn More
                </Button>
              </div>
            </div>

            <div className="hidden lg:grid grid-cols-2 gap-4 animation-delay-400 animate-fade-in-up">
              <Card className="glass-card p-6">
                <div className="text-[#4E80FF] mb-2">
                  <Layers className="w-8 h-8" />
                </div>
                <p className="font-heading text-3xl font-bold text-slate-900">
                  {stats.total_area_acres.toFixed(0)}+
                </p>
                <p className="text-slate-600 text-sm">Acres Pooled</p>
              </Card>
              <Card className="glass-card p-6">
                <div className="text-[#2952CC] mb-2">
                  <Users className="w-8 h-8" />
                </div>
                <p className="font-heading text-3xl font-bold text-slate-900">
                  {stats.total_users}+
                </p>
                <p className="text-slate-600 text-sm">Landowners</p>
              </Card>
              <Card className="glass-card p-6">
                <div className="text-[#4E80FF] mb-2">
                  <Building className="w-8 h-8" />
                </div>
                <p className="font-heading text-3xl font-bold text-slate-900">
                  {stats.total_consortiums}+
                </p>
                <p className="text-slate-600 text-sm">Active Consortiums</p>
              </Card>
              <Card className="glass-card p-6">
                <div className="text-[#2952CC] mb-2">
                  <TrendingUp className="w-8 h-8" />
                </div>
                <p className="font-heading text-3xl font-bold text-slate-900">98%</p>
                <p className="text-slate-600 text-sm">Success Rate</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Complete Land Pooling Solution
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to manage land pooling and consortium operations
              in one integrated platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group card-hover border-slate-200"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div
                    className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A simple, transparent process to pool your land and join the
              development journey.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative text-center">
                {/* Connector Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-200" />
                )}
                <div className="relative z-10">
                  <div
                    className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white font-heading font-bold text-xl mb-4 ${
                      index % 2 === 0 ? "bg-[#4E80FF]" : "bg-[#2952CC]"
                    }`}
                  >
                    {step.step}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consortium Management Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-50 text-[#4E80FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Users className="w-4 h-4" />
                Consortium Management™
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Professional Administration for Land Consortiums
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our comprehensive consortium management module provides all the
                tools needed for effective collaboration between landowners,
                representatives, and developers.
              </p>
              <div className="space-y-4">
                {[
                  "Digital agreement formats with lawyer support",
                  "Meeting coordination with agenda & minutes",
                  "Weighted voting for fair decision-making",
                  "RFP creation and bid management",
                  "Secure consent with OTP verification",
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#4E80FF] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <Button
                className="btn-secondary mt-8"
                onClick={() => navigate("/services")}
                data-testid="explore-services-btn"
              >
                Explore Services
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg"
                alt="Business meeting"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Vote className="w-5 h-5 text-[#4E80FF]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Voting Active</p>
                    <p className="text-sm text-slate-500">12 members voted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#4E80FF] to-[#2952CC]">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Pool Your Land?
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
            Join thousands of landowners who have transformed their properties
            through our intelligent land pooling platform.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-[#4E80FF] hover:bg-white/90 text-lg px-8"
              onClick={() => navigate("/register")}
              data-testid="cta-register-btn"
            >
              Register Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8"
              onClick={() => navigate("/knowledge-hub")}
              data-testid="cta-learn-btn"
            >
              Learn About Land Pooling
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
